import { useState } from 'react';
import { Brain, ChevronDown, ChevronUp, Pencil, Plus, Trash2, Zap } from 'lucide-react';
import type { JourneyItemTemplateFirestore, JourneyItemType, JourneySectionTemplateFirestore } from '../services/journeyTypes';
import { journeyService } from '../services/journeyService';
import { DisplayJourneyItem } from './DisplayJourneyItem';
import { EditJourneyItem } from './EditJourneyItem';
import { JourneyItemTypeSelector } from './JourneyItemTypeSelector';
import { MoveJourneyItemModal } from './MoveJourneyItemModal';
import { EditSectionModal } from './EditSectionModal';

interface SectionAccordionProps {
    section: JourneySectionTemplateFirestore;
    items: JourneyItemTemplateFirestore[];
    chapterId: string;
    isOpen: boolean;
    onToggle: () => void;
    onItemUpdated: () => void;
}

const SECTION_BG_CLASS: Record<string, string> = {
    yellow: 'bg-yellow-100 hover:bg-yellow-200',
    blue: 'bg-blue-100 hover:bg-blue-200',
    green: 'bg-green-100 hover:bg-green-200',
    pink: 'bg-pink-100 hover:bg-pink-200',
};

export const SectionAccordion = ({ section, items, chapterId, isOpen, onToggle, onItemUpdated }: SectionAccordionProps) => {
    const [editingItem, setEditingItem] = useState<JourneyItemTemplateFirestore | null>(null);
    const [creatingType, setCreatingType] = useState<JourneyItemType | null>(null);
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [movingItem, setMovingItem] = useState<JourneyItemTemplateFirestore | null>(null);
    const [isEditingSection, setIsEditingSection] = useState(false);

    const bgClass = SECTION_BG_CLASS[section.color] ?? 'bg-gray-100 hover:bg-gray-200';
    const totalBrainPoints = items.reduce((sum, item) => sum + item.brainPoints, 0);
    const totalEnergyCost = items.reduce((sum, item) => sum + item.energyCost, 0);
    const nextOrder = items.length ? Math.max(...items.map((item) => item.order)) + 1 : 0;

    const closeEditor = () => {
        setEditingItem(null);
        setCreatingType(null);
    };

    const handleSave = async (item: JourneyItemTemplateFirestore) => {
        if (item.id) {
            await journeyService.updateItem(chapterId, section.id!, item);
        } else {
            await journeyService.createItem(chapterId, section.id!, item);
        }
        closeEditor();
        onItemUpdated();
    };

    const handleSaveAndContinue = async (item: JourneyItemTemplateFirestore) => {
        let saved = item;
        if (item.id) {
            await journeyService.updateItem(chapterId, section.id!, item);
        } else {
            saved = await journeyService.createItem(chapterId, section.id!, item);
        }
        setCreatingType(null);
        setEditingItem(saved);
        onItemUpdated();
    };

    const handleReorder = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= items.length) return;
        const reordered = [...items];
        [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
        const withOrders = reordered.map((item, i) => ({ ...item, order: i }));
        await journeyService.updateItemOrders(chapterId, section.id!, withOrders);
        onItemUpdated();
    };

    const handleMove = async (targetChapterId: string, targetSectionId: string) => {
        if (!movingItem) return;
        await journeyService.moveItem(chapterId, section.id!, targetChapterId, targetSectionId, movingItem);
        setMovingItem(null);
        onItemUpdated();
    };

    const handleSectionSave = async (updated: JourneySectionTemplateFirestore) => {
        await journeyService.updateSection(chapterId, updated);
        setIsEditingSection(false);
        onItemUpdated();
    };

    const handleSectionDelete = async () => {
        if (!section.id) return;
        const confirmed = window.confirm(
            `Delete section "${section.header}"? This permanently deletes the section and ALL ${items.length} items. This cannot be undone.`,
        );
        if (!confirmed) return;
        await journeyService.deleteSection(chapterId, section.id);
        onItemUpdated();
    };

    return (
        <div>
            <div
                className={`flex justify-between items-center gap-2 rounded-lg py-2 px-3 cursor-pointer transition-colors ${bgClass}`}
                onClick={onToggle}
            >
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <h3 className="text-md font-semibold text-gray-700 break-words">{section.header}</h3>
                    {section.id && (
                        <span className="text-xs font-mono text-gray-400 break-all">id: {section.id}</span>
                    )}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditingSection(true);
                        }}
                        className="p-1 rounded hover:bg-gray-300 transition-colors text-gray-500 hover:text-gray-800"
                        title="Edit section"
                    >
                        <Pencil size={14} />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSectionDelete();
                        }}
                        className="p-1 rounded hover:bg-gray-300 transition-colors text-gray-500 hover:text-red-600"
                        title="Delete section"
                    >
                        <Trash2 size={14} />
                    </button>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                        {items.length} items
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                        <Brain className="w-3 h-3" />
                        {totalBrainPoints}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Zap className="w-3 h-3" />
                        {totalEnergyCost}
                    </span>
                </div>
                {isOpen ? <ChevronUp size={16} className="shrink-0" /> : <ChevronDown size={16} className="shrink-0" />}
            </div>

            {isOpen && (
                <div className="mt-2 space-y-2 ml-2">
                    {items.map((item, index) => (
                        <DisplayJourneyItem
                            key={item.id}
                            item={item}
                            chapterId={chapterId}
                            sectionId={section.id!}
                            onDeleted={onItemUpdated}
                            onMove={() => setMovingItem(item)}
                            onMoveUp={() => handleReorder(index, 'up')}
                            onMoveDown={() => handleReorder(index, 'down')}
                            canMoveUp={index > 0}
                            canMoveDown={index < items.length - 1}
                            onClick={() => setEditingItem(item)}
                        />
                    ))}
                    {items.length === 0 && (
                        <p className="text-gray-400 text-sm italic ml-2">No items in this section.</p>
                    )}
                    <button
                        onClick={() => setShowTypeSelector(true)}
                        className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors ml-2 mt-1"
                    >
                        <Plus size={16} />
                        Add Item
                    </button>
                </div>
            )}

            {showTypeSelector && (
                <JourneyItemTypeSelector
                    onSelect={(type) => {
                        setCreatingType(type);
                        setShowTypeSelector(false);
                    }}
                    onCancel={() => setShowTypeSelector(false)}
                />
            )}

            {(editingItem || creatingType) && (
                <EditJourneyItem
                    itemTemplate={editingItem ?? undefined}
                    itemType={editingItem?.type ?? creatingType!}
                    initialOrder={editingItem?.order ?? nextOrder}
                    onSave={handleSave}
                    onSaveAndContinue={handleSaveAndContinue}
                    onCancel={closeEditor}
                    sectionHeader={section.header}
                />
            )}

            {movingItem && (
                <MoveJourneyItemModal
                    currentSectionId={section.id!}
                    onMove={handleMove}
                    onCancel={() => setMovingItem(null)}
                />
            )}

            {isEditingSection && (
                <EditSectionModal
                    section={section}
                    onSave={handleSectionSave}
                    onCancel={() => setIsEditingSection(false)}
                />
            )}
        </div>
    );
};
